// 📄 components/StepRFC.tsx
import React, { useState } from 'react';
import { Play, RefreshCw, Server, Shield, FileText, Globe } from 'lucide-react';
import { GeneralsFormData } from '../../../../../services/commerce/integrationService.ts';

interface StepRFCProps {
  rfcDestination: string;
  connectionType: string;
  onChange: (field: keyof GeneralsFormData, value: any) => void;
  testingConnection: boolean;
  connectionStatus: 'idle' | 'success' | 'error';
  onTestConnection: () => void;
  isInvalid: boolean;
}

export function StepRFC({
  rfcDestination,
  connectionType,
  onChange,
  testingConnection,
  connectionStatus,
  onTestConnection,
  isInvalid
}: StepRFCProps) {
  // 🔘 Estado para controlar las pestañas internas estilo SM59
  const [internalTab, setInternalTab] = useState<'technical' | 'security' | 'testResult'>('technical');

  // Estados simulados locales para que el técnico interactúe con el "clon" de la SM59
  const [host, setHost] = useState('test-integration.us.managedgateway.cloud.sap');
  const [port, setPort] = useState('443');
  const [pathPrefix, setPathPrefix] = useState('/cxf/receiveIDOC');
  const [sapUser, setSapUser] = useState('P000607');

  return (
    <div className="space-y-4">
      {/* Guía de Ruta SPRO de SAP */}
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 mb-2 space-y-1">
        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block">Ruta SPRO / Transacción</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-gray-400 font-bold mr-1.5">SM59</span> 
          <span>HTTP Connections to External Server (Tipo G)</span>
        </p>
      </div>

      <h3 className="text-lg font-medium text-blue-400">2. Configuración de Destino RFC / HTTP</h3>
      
      {/* Datos Básicos Generales de la SM59 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white/5 p-4 rounded-xl border border-white/5">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">RFC Destination</label>
          <input
            type="text"
            value={rfcDestination}
            onChange={(e) => onChange('rfcDestination', e.target.value)}
            placeholder="ej. CIG_QS4"
            className={`w-full bg-zinc-800 border rounded-lg p-2 text-sm focus:outline-none transition-all text-white ${
              isInvalid && !rfcDestination ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Connection Type</label>
          <select 
            value={connectionType} 
            onChange={(e) => onChange('connectionType', e.target.value)} 
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2 text-sm focus:outline-none text-white"
          >
            <option value="G">G - HTTP Connection to External Server</option>
            <option value="3">3 - ABAP Connection</option>
          </select>
        </div>
      </div>

      {/* 📑 SUB-SISTEMA DE PESTAÑAS (Clon de SAP GUI) */}
      <div className="border border-white/10 rounded-xl overflow-hidden bg-zinc-900/50 mt-4">
        {/* Barra de Pestañas */}
        <div className="flex bg-zinc-800/80 border-b border-white/10 p-1 gap-1">
          <button
            type="button"
            onClick={() => setInternalTab('technical')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              internalTab === 'technical' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Server className="w-3.5 h-3.5" /> Technical Settings
          </button>
          <button
            type="button"
            onClick={() => setInternalTab('security')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              internalTab === 'security' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Shield className="w-3.5 h-3.5" /> Logon & Security
          </button>
          <button
            type="button"
            onClick={() => setInternalTab('testResult')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              internalTab === 'testResult' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'text-gray-400 hover:text-white'
            }`}
          >
            <FileText className="w-3.5 h-3.5" /> Connection Test Result
          </button>
        </div>

        {/* Contenido de cada Pestaña */}
        <div className="p-4 min-h-[160px]">
          {/* Pestana 1: Technical Settings */}
          {internalTab === 'technical' && (
            <div className="space-y-3 animate-fadeIn">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">Target System Settings</span>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-[10px] text-gray-400 mb-0.5">Target Host</label>
                  <input type="text" value={host} onChange={(e) => setHost(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-xs font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">Service Port</label>
                  <input type="text" value={port} onChange={(e) => setPort(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-xs font-mono" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Path Prefix</label>
                <input type="text" value={pathPrefix} onChange={(e) => setPathPrefix(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-xs font-mono" />
              </div>
            </div>
          )}

          {/* Pestana 2: Logon & Security */}
          {internalTab === 'security' && (
            <div className="space-y-3 animate-fadeIn">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">Logon Procedure (Basic Authentication)</span>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">User</label>
                  <input type="text" value={sapUser} onChange={(e) => setSapUser(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-xs font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">Password Status</label>
                  <input type="text" disabled value="saved / is initial" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-xs font-mono text-gray-500 cursor-not-allowed" />
                </div>
              </div>
              <div className="pt-2 border-t border-white/5">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Security Options</span>
                <div className="flex items-center gap-4 text-xs text-gray-300">
                  <label className="flex items-center gap-1.5"><input type="radio" checked disabled /> SSL Active</label>
                  <span className="text-gray-500 font-mono text-[11px]">SSL Certificate: ANONYM SSL Client</span>
                </div>
              </div>
            </div>
          )}

          {/* Pestana 3: Connection Test Result */}
          {internalTab === 'testResult' && (
            <div className="space-y-3 animate-fadeIn">
              <div className="flex justify-between items-center mb-1">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">Resultados analíticos del ping</span>
                <button 
                  type="button"
                  onClick={() => {
                    onTestConnection();
                  }} 
                  disabled={testingConnection}
                  className="flex items-center gap-1.5 bg-blue-500 text-slate-950 px-2.5 py-1 rounded text-xs font-bold hover:bg-blue-600 transition-all disabled:bg-gray-600"
                >
                  {testingConnection ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  Ejecutar Test Connection
                </button>
              </div>

              {connectionStatus === 'idle' && !testingConnection && (
                <p className="text-xs text-gray-500 italic text-center py-4">Haz clic en ejecutar para simular la respuesta del Gateway de Ariba.</p>
              )}

              {testingConnection && (
                <div className="flex flex-col items-center justify-center py-4 space-y-2">
                  <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
                  <span className="text-xs text-gray-400 font-mono">PING {host}...</span>
                </div>
              )}

              {connectionStatus === 'success' && !testingConnection && (
                <div className="overflow-hidden border border-white/10 rounded-lg text-xs font-mono">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-white/5 text-gray-400 text-[10px] uppercase">
                        <th className="p-2 border-b border-white/10">SAP SM59 Detail Fields</th>
                        <th className="p-2 border-b border-white/10">Value Mapping</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      <tr className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-2 text-orange-400 font-medium">Status HTTP Response</td>
                        <td className="p-2 bg-red-500/10 text-red-400 font-bold">500 (Simulated Gateway Target)</td>
                      </tr>
                      <tr className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-2">Status Text</td>
                        <td className="p-2 text-gray-400">Internal Server Error (No Payload Passed)</td>
                      </tr>
                      <tr className="hover:bg-white/5">
                        <td className="p-2">Test Call Duration</td>
                        <td className="p-2 text-blue-400">1,715 ms</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}